import { AlertCircle, Mail } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import { useOneClickLoginCode } from "../context/OneClickLoginCodeContext";
import { scrollToElement } from "../hooks/useScrollManagement";
//

import { zodResolver } from "@hookform/resolvers/zod";
import { useAnalytics } from "../hooks/useAnalytics";
import { useContactForm } from "../hooks/useContactForm";
import { TrialIdConfig } from "../types/landingPage";
import { getCountryByCode } from "../utils/validationUtils";
import { useLandingPageConfig } from "../hooks/useLandingPageConfig";
// removed getMatchingTrialId usage on this page
import {
  ExistingPatientContactFormSchema,
  existingPatientContactFormSchema,
  newPatientContactFormSchema,
  NewPatientContactFormSchema,
} from "./contact-form/contactFormSchema";
import { ContactFormStatusMessage } from "./contact-form/FormStatusMessage";
import { QuestionnaireAnswers } from "./contact-form/utils/questionnaireStorage";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GoogleLoginButton } from "./GoogleLoginButton";
import { useAuth } from "../context/AuthContext";
import { VerificationCodeVariantB  } from "./contact-form/VerificationCodeVariantB";
import { getContactFormState, setContactFormState, populateContactFormStateFromUser } from "./contact-form/utils/contactFormStorage";
import useLandingPageIds from "../hooks/useLandingPageIds";
import { identify } from "../utils/rudderstack";

interface ContactFormProps {
  questionnaireAnswers?: QuestionnaireAnswers;
  landingPageId: string;
  countryCode: string;
  trialIdConfig?: string | TrialIdConfig;
}

/**
 * Contact form variant A component for collecting user information
 */
export function ContactInformation({
  questionnaireAnswers = {},
  landingPageId,
  countryCode,
}: ContactFormProps) {
  const selectedCountry = getCountryByCode(countryCode);
  const { oneClickLoginCode } = useOneClickLoginCode();
  const isNewPatient = !oneClickLoginCode;
  const {
    formattedErrorMessage,
    submissionError,
  } = useContactForm({
    questionnaireAnswers,
    landingPageId,
    selectedCountry,
  });

  const {
    register,
    formState: { errors },
    setValue,
    watch,
  } = useForm<ExistingPatientContactFormSchema | NewPatientContactFormSchema>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    resolver: zodResolver(
      isNewPatient
        ? newPatientContactFormSchema
        : existingPatientContactFormSchema,
    ),
    defaultValues: {
      country: countryCode,
      email: getContactFormState(landingPageId).email || "",
      ethnicity: [],
      termsAccepted: false,
      phoneValid: false,
    },
  });
  const typedErrors = errors as FieldErrors<NewPatientContactFormSchema>;
  const emailValue = (watch("email") as string) || "";
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);

  const errorMessageRef = useRef<HTMLDivElement>(null);

  const [useGoogleLogin, setUseGoogleLogin] = React.useState(false);
  const [googleEmail, setGoogleEmail] = React.useState<string>("");

  const { track } = useAnalytics();
  const { loginWithGoogleJwt, registerWithEmail, sendLoginCode, loginWithCode } = useAuth();
  const pageConfig = useLandingPageConfig();

  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  // Track when contact form is viewed
  useEffect(() => {
    track("lp.contact_form.viewed", {
      is_eligible: questionnaireAnswers?.prescreeningStatus === "passed",
      has_prescreener: Object.keys(questionnaireAnswers?.questionAnswers || {}).length > 0,
      landing_page_id: landingPageId,
      useContactPageVariantA: true,
      variant: "A", // Track which variant was used
    });
  }, []); // Only track once on mount

  // Persist email on change
  useEffect(() => {
    const sub = watch((values, info) => {
      if (info.name === "email") {
        const v: any = values;
        setContactFormState(landingPageId, { email: v?.email || undefined });
      }
    });
    return () => sub.unsubscribe();
  }, [watch, landingPageId]);

  // Function to handle input focus and scroll the element into view
  const handleInputFocus = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLSelectElement | HTMLButtonElement
    >,
  ) => {
    setTimeout(() => {
      scrollToElement(e.target, { offset: window.innerHeight * 0.3 });
    }, 100);
  };

  const [showVerification, setShowVerification] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verificationError, setVerificationError] = useState<string | undefined>();
  const [verificationData, setVerificationData] = useState<{
    loginCodeDestination?: string | null;
    loginCodeFlowType?: 'EMAIL' | 'SMS' | null;
  } | null>(null);
  const [hasTrackedVerificationView, setHasTrackedVerificationView] = useState(false);
  const { variantId, masterId } = useLandingPageIds();
    
  const emailHint = useMemo(() => emailValue || googleEmail || undefined, [emailValue, googleEmail]);

  const handleResendLoginCode = async (preferredLoginType?: "email" | "sms") => {
    if (!emailHint) return;
    setSendingCode(true);
    setVerificationError(undefined);
    try {
      const result = await sendLoginCode(emailHint, (preferredLoginType?.toUpperCase() as any) || "EMAIL");
      if (result) {
        setVerificationData(result);

        if (!hasTrackedVerificationView) {
          // Track verification screen view
          track("lp.verification.screen_viewed", {
            landing_page_id: landingPageId,
            variant_id: variantId,
            master_id: masterId,
            has_questionnaire_data: hasQuestionnaire,
            direct_load: true,
            useContactPageVariantA: true,
          });
    
          // Mark as tracked to avoid duplicate events
          setHasTrackedVerificationView(true);
        }
}
    } catch (e: any) {
      setVerificationError(e?.message || "Failed to send code");
    } finally {
      setSendingCode(false);
    }
  };
  
  const hasQuestionnaire =
    !!questionnaireAnswers.questionAnswers &&
    Object.keys(questionnaireAnswers.questionAnswers).length > 0;

  const handleVerifyCode = async (code: string) => {
    if (!emailHint) return;
    setSendingCode(true);
    setVerificationError(undefined);
    try {
      const user = await loginWithCode(emailHint, code);
      populateContactFormStateFromUser(landingPageId, user);
      if (!user) {
        setVerificationError("Invalid code. Please try again.");
        track("lp.verification.code_invalid", {
          landing_page_id: landingPageId,
          has_questionnaire_data: hasQuestionnaire,
          useContactPageVariantA: true,
        });        
        return;
      }
      track("lp.verification.completed", {
        landing_page_id: landingPageId,
        has_questionnaire_data: hasQuestionnaire,
        useContactPageVariantA: true,
        isNewPatient: user.termsVersion === null,
      });     
      identify(user.id);
      // on success, enable navigation to next step
      const id = params.id as string;
      const search = location.search || "";
      navigate(`/${id}/personal${search}`);
    } catch (e: any) {
      setVerificationError(e?.message || "Verification failed");
    } finally {
      setSendingCode(false);
    }
  };

  // Handle Google login success
  const handleGoogleLogin = async (credential: string) => {
    try {
      // Try to authenticate using Google JWT directly
      const user = await loginWithGoogleJwt(credential, {
        conditionId: pageConfig?.conditionId ?? 0,
        countryCode: pageConfig?.countryCode,
        locale: "EN",
      });
      if (!user) {
        setVerificationError("Invalid google credentials. Please try again.");
        track("lp.verification.google_error", {
          landing_page_id: landingPageId,
          has_questionnaire_data: hasQuestionnaire,
          useContactPageVariantA: true,
        });        
        return;
      }      
      populateContactFormStateFromUser(landingPageId, user);
      if (user?.email) {
        setGoogleEmail(user.email);
        setUseGoogleLogin(true);
        setValue("email", user.email, { shouldValidate: true, shouldDirty: true });
      }
      track("lp.verification.google_completed", {
        landing_page_id: landingPageId,
        useContactPageVariantA: true,
        isNewPatient: user?.termsVersion === null,
        variant: "A",
      });
      identify(user.id);
      // Ensure we are on the ContactInformation page after auth
      const id = params.id as string;
      const search = location.search || "";
      navigate(`/${id}/contact${search}`, { replace: true });
    } catch (error) {
      console.error("Failed to decode Google credential:", error);
      track("lp.verification.google_error", {
        landing_page_id: landingPageId,
        variant: "A",
      });
    }
  };

  const handleContinue = () => {
    track("lp.contact_information.continue", {
      landing_page_id: landingPageId,
      variant: "A",
    });
    if (isNewPatient && isEmailValid) {
      // Attempt to register a new patient with the provided email
      if (pageConfig?.conditionId) {
        registerWithEmail({
          conditionId: pageConfig.conditionId,
          email: emailValue,
          countryCode: pageConfig.countryCode,
          locale: "EN",
        })
          .then((user) => {
            populateContactFormStateFromUser(landingPageId, user);
            if (user && user.id) {
              identify(user.id);
            }
            // success: proceed
            const id = params.id as string;
            const search = location.search || "";
            navigate(`/${id}/personal${search}`);
          })
          .catch(async (e: any) => {
            const exists =
              e?.graphQLErrors?.some(
                (err: any) =>
                  err?.message === "User with this email already exists" ||
                  err?.extensions?.detailedMessage === "User with this email already exists",
              ) || false;
            if (exists) {
              // Send login code and show verification UI
              setShowVerification(true);
              try {
                await handleResendLoginCode("sms");
              } catch {}
              return;
            }
            // other errors: fall back to default navigation to allow user to continue filling
            const id = params.id as string;
            const search = location.search || "";
            navigate(`/${id}/personal${search}`);
          });
        return;
      }
    }
    track("lp.contact_information.continue_clicked", {
      landing_page_id: landingPageId,
      variant: "A",
    });
    if (!showVerification) {
      const id = params.id as string;
      const search = location.search || "";
      navigate(`/${id}/personal${search}`);
    }
  };

  useEffect(() => {
    if (formattedErrorMessage && errorMessageRef.current) {
      setTimeout(() => {
        scrollToElement(errorMessageRef.current!, {
          behavior: "smooth",
          block: "start",
          offset: window.innerHeight * 0.2,
        });
      }, 100);
    }
  }, [formattedErrorMessage]);

  return (
    <div
      className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100"
      data-test-id="contact-form-container-variant-a"
    >
      <div className="p-6">
        <ContactFormStatusMessage
          status={questionnaireAnswers.prescreeningStatus}
        />

        {/* API errors - show both parent errors and form submission errors */}
        {formattedErrorMessage && (
          <div
            ref={errorMessageRef}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700 flex items-start animate-fade-in"
            data-test-id="contact-form-error"
          >
            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium">Form submission failed</p>
              <p className="text-sm mt-1">{formattedErrorMessage}</p>

              {/* Show specific guidance based on error type */}
              {submissionError?.name === "NetworkError" && (
                <div className="mt-3 p-2 bg-red-100 rounded text-xs">
                  <p className="font-medium">Network Error</p>
                  <ul className="list-disc ml-4 mt-1 space-y-1">
                    <li>Check your internet connection</li>
                    <li>
                      Ensure you're not using a VPN that might be blocking the
                      request
                    </li>
                    <li>Try refreshing the page and submitting again</li>
                  </ul>
                </div>
              )}

              {submissionError?.name === "ApiError" &&
                submissionError.response &&
                "status" in submissionError.response && (
                  <p className="text-xs mt-2">
                    Error code:{" "}
                    {typeof submissionError.response.status === "number"
                      ? submissionError.response.status
                      : "Unknown"}
                  </p>
                )}
            </div>
          </div>
        )}

        <form 
          className="space-y-8" 
          data-test-id="contact-form-variant-a"
          onSubmit={(e) => {
            e.preventDefault();
            handleContinue();
          }}
        >
          {/* Contact Information Section - Moved to Top for Variant A */}
          <div
            className="space-y-6 bg-white rounded-lg p-6 shadow-sm border border-gray-100"
            data-test-id="contact-info-section"
          >
            <div className="flex items-center space-x-2 pb-2 border-b border-gray-200">
              <Mail className="w-5 h-5 text-primary-500" />
              <h3 className="text-lg font-medium text-gray-900">
                Contact Information
              </h3>
            </div>
            <div className="space-y-6">
              {isNewPatient && (
                <div>
                  {useGoogleLogin ? (
                    // Show Google login result with option to change
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center justify-between p-3 border border-green-300 bg-green-50 rounded-md">
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 text-green-600 mr-2" />
                          <span className="text-sm font-medium text-green-800">{googleEmail}</span>
                          <span className="ml-2 text-xs text-green-600">(via Google)</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setUseGoogleLogin(false);
                            setGoogleEmail("");
                            setValue("email", "", { shouldValidate: true, shouldDirty: true });
                          }}
                          className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Show email input first, then Google login option
                    <div className="space-y-4">
                      {/* Manual email input */}
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Email <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1 relative rounded-md shadow-sm max-w-md" style={{ width: '400px' }} >
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Mail className="h-4 w-4 text-gray-400" />
                          </div>
                          <input
                            type="email"
                            id="email"
                            data-test-id="input-email"
                            {...register("email")}
                            onFocus={handleInputFocus}
                            readOnly={showVerification}
                            className={`block w-full pl-10 pr-20 border ${
                              typedErrors.email
                                ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                                : showVerification
                                ? "border-gray-200 bg-gray-50 cursor-not-allowed text-gray-500"
                                : "border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                            } rounded-md shadow-sm py-2 px-3 focus:outline-none sm:text-sm transition-colors duration-200`}
                            placeholder="you@example.com"
                            style={{ width: '400px' }}
                          />
                            {showVerification && (
                              <button
                                type="button"
                                onClick={() => {
                                  setShowVerification(false);
                                  // Focus the email input after a short delay to ensure state update
                                  setTimeout(() => {
                                    const emailInput = document.getElementById('email') as HTMLInputElement;
                                    if (emailInput) {
                                      emailInput.focus();
                                    }
                                  }, 0);
                                }}
                                className="absolute inset-y-0 right-1 px-3 text-sm text-primary-600 hover:text-primary-800 font-medium bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                                style={{ background: 'none' }}
                              >
                                Change
                              </button>
                            )}
                        </div>
                        {typedErrors.email && (
                          <div className="text-red-500 text-sm mt-1 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {typedErrors.email.message}
                          </div>
                        )}
                      </div>

                      {showVerification && verificationData && (
                        <div className="pt-4 max-w-md">
                        <VerificationCodeVariantB 
                            onSubmit={handleVerifyCode}
                            onResendCode={handleResendLoginCode}
                            smsHint={verificationData?.loginCodeDestination || ""}
                            emailHint={emailHint}
                            error={verificationError}
                            isLoading={sendingCode}
                            verificationType={(verificationData?.loginCodeFlowType?.toLowerCase() as "email" | "sms") || "sms"}
                            smsLoginSupported={verificationData?.loginCodeFlowType === 'SMS'}
                          />
                        </div>
                      )}

                      {/* Google login alternative */}
                      <div className="text-center" style={{ width: '400px' }}>
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300" />
                          </div>
                          <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white text-gray-500">OR</span>
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          <GoogleLoginButton
                            onSuccess={handleGoogleLogin}
                            onError={(error) => {
                              console.error("Google login error:", error);
                              track("lp.verification.google_error", {
                                landing_page_id: landingPageId,
                                variant: "A",
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Hidden Fields now handled on PersonalInformation page */}
            </div>
          </div>
          {/* Location section moved to PersonalInformation page */}

          {/* Continue Button */}
          {!showVerification && (
            <div className="pt-4">
              <button
                type="button"
                data-test-id="contact-information-continue"
                onClick={handleContinue}
                disabled={isNewPatient && (!emailValue || !isEmailValid)}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white ${
                  formattedErrorMessage
                    ? "bg-red-600 hover:bg-red-700 focus:ring-red-500"
                    : "bg-primary-600 hover:bg-primary-700 focus:ring-primary-500"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  isNewPatient && (!emailValue || !isEmailValid) ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                Continue
              </button>
            </div>
          )}

        </form>
      </div>
    </div>
  );
}